import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { FleetVehicle, FleetKpis } from './models/fleet.models';
import { FLEET_SEED } from './fleet-seed.data';

/**
 * FleetMockStore — ÚNICA fuente de verdad de la flota simulada.
 *
 * `providedIn: 'root'` ⇒ una sola instancia en toda la app. Todas las vistas
 * (tabla, mapa, mini-tarjeta, analíticas, perfil 360) se suscriben a los
 * mismos observables, por lo que es imposible que se desincronicen: si la
 * placa HCM8263 cambia a STOPPED, todas reaccionan a la vez.
 *
 * La firma pública (`fleet$`, `vehicle$(id)`, `kpis$`) imita la de un servicio
 * real: el día que exista backend, se cambia sólo el origen (FLEET_SEED →
 * AxiosService) sin tocar los componentes.
 */
@Injectable({ providedIn: 'root' })
export class FleetMockStore {
  private readonly _fleet = new BehaviorSubject<FleetVehicle[]>(structuredClone(FLEET_SEED));

  /** Flujo principal: toda la flota. */
  readonly fleet$: Observable<FleetVehicle[]> = this._fleet.asObservable();

  /** Sólo vehículos en movimiento (para el mapa vivo). */
  readonly moving$: Observable<FleetVehicle[]> = this.fleet$.pipe(map((v) => v.filter((x) => x.status_live === 'MOVING')));

  /** KPIs agregados de flota (para el dashboard analítico). */
  readonly kpis$: Observable<FleetKpis> = this.fleet$.pipe(map((fleet) => this.deriveKpis(fleet)));

  /** Un vehículo por id (para el perfil 360 y las analíticas por-vehículo). */
  vehicle$(id: string): Observable<FleetVehicle | undefined> {
    return this.fleet$.pipe(map((fleet) => fleet.find((v) => v.id === id)));
  }

  /** Snapshot sincrónico (lo usa el simulador). */
  snapshot(): FleetVehicle[] {
    return this._fleet.getValue();
  }

  /** Actualiza un vehículo y re-emite a todos los suscriptores. */
  patch(id: string, delta: Partial<FleetVehicle>): void {
    const next = this._fleet.getValue().map((v) => (v.id === id ? { ...v, ...delta } : v));
    this._fleet.next(next);
  }

  /** Elimina un vehículo (usado por manage-vehicles). */
  remove(id: string): void {
    this._fleet.next(this._fleet.getValue().filter((v) => v.id !== id));
  }

  /** Reemplaza el conjunto usado por el simulador tras mover coordenadas. */
  setAll(vehicles: FleetVehicle[]): void {
    this._fleet.next(vehicles);
  }

  private deriveKpis(fleet: FleetVehicle[]): FleetKpis {
    const total = fleet.length;
    const moving = fleet.filter((v) => v.status_live === 'MOVING').length;
    const stopped = fleet.filter((v) => v.status_live === 'STOPPED').length;
    const idle = fleet.filter((v) => v.status_live === 'IDLE').length;

    const avg = (arr: number[]) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
    const avgFuelEfficiency = Math.round(avg(fleet.map((v) => v.analytics.fuelEfficiencyKmpl)) * 10) / 10;
    const totalIdleHours = fleet.reduce((a, v) => a + v.analytics.idleTimeHours, 0);
    const avgCostPerKm = Math.round(avg(fleet.map((v) => v.analytics.costPerKm)));

    const maintenanceAlerts = fleet.reduce((a, v) => a + v.maintenance.filter((m) => m.alert).length, 0);

    // Suma mensual de combustible de toda la flota (12 meses).
    const fuelCostSeries = Array.from({ length: 12 }, (_, i) => fleet.reduce((a, v) => a + (v.analytics.monthlyFuelCost[i] ?? 0), 0));

    const costliestPerKm = [...fleet]
      .sort((a, b) => b.analytics.costPerKm - a.analytics.costPerKm)
      .slice(0, 3)
      .map((v) => ({ plate: v.plate ?? '—', label: `${v.brand} ${v.vehicle_model}`, costPerKm: v.analytics.costPerKm }));

    return { total, moving, stopped, idle, avgFuelEfficiency, totalIdleHours, avgCostPerKm, maintenanceAlerts, fuelCostSeries, costliestPerKm };
  }
}
